/**
 * ARIF PREMIUM PRODUCT — Core Interaction Engine
 * Sona Sansaar — Luxury Jewellery (New Zealand)
 * 
 * Zero jQuery dependency. Native IntersectionObserver. Async cart/add.js.
 * Dispatches 'cart:updated' CustomEvent for theme cart drawer integration.
 * 
 * @version 1.0.0
 */
const ArifProduct = (function () {
  'use strict';

  /* -----------------------------------------------------------------------
     INITIALIZATION
     ----------------------------------------------------------------------- */
  function init() {
    initVariantPicker();
    initAccordions();
    initStickyPanel();
    initFormInteractions();
    initGallery();
  }

  /* -----------------------------------------------------------------------
     VARIANT PICKER — Pill selection → JSON variant matching → UI update
     ----------------------------------------------------------------------- */
  function initVariantPicker() {
    const container = document.querySelector('.arif-variant-picker-matrix');
    if (!container) return;

    container.addEventListener('change', function (e) {
      if (!e.target.matches('.arif-variant-input-hidden')) return;

      /* Update the inline preview label */
      const group = e.target.closest('.arif-variant-option-group');
      if (group) {
        const preview = group.querySelector('.arif-selected-value-preview');
        if (preview) preview.textContent = e.target.value;
      }

      evaluateCurrentOptions();
    });
  }

  function evaluateCurrentOptions() {
    const jsonContainer = document.querySelector('[id^="ArifVariantJson-"]');
    if (!jsonContainer) return;

    var variants;
    try {
      variants = JSON.parse(jsonContainer.textContent);
    } catch (err) {
      return;
    }

    var selectedOptions = Array.from(
      document.querySelectorAll('.arif-variant-input-hidden:checked')
    ).map(function (el) {
      return el.value;
    });

    var matched = variants.find(function (v) {
      return v.options.every(function (opt, i) {
        return opt === selectedOptions[i];
      });
    });

    updateUIForVariant(matched);
  }

  function updateUIForVariant(variant) {
    var formInput = document.querySelector('#arif-main-product-form input[name="id"]');
    var submitBtn = document.querySelector('.arif-add-to-cart-cta');
    var priceContainer = document.querySelector('.arif-price-container');

    if (!variant) {
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.querySelector('span').textContent = 'UNAVAILABLE';
      }
      return;
    }

    /* Update hidden form variant ID */
    if (formInput) formInput.value = variant.id;

    /* Update price display */
    if (priceContainer) {
      var currentPriceEl = priceContainer.querySelector('.arif-current-price');
      var comparePriceEl = priceContainer.querySelector('.arif-compare-price');
      var discountBadgeEl = priceContainer.querySelector('.arif-discount-badge');
      var installmentsEl = priceContainer.querySelector('.arif-installments-text');

      if (currentPriceEl) currentPriceEl.textContent = formatMoney(variant.price);

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (comparePriceEl) {
          comparePriceEl.textContent = formatMoney(variant.compare_at_price);
          comparePriceEl.style.display = '';
        }
        if (discountBadgeEl) {
          var pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          discountBadgeEl.textContent = 'SAVE ' + pct + '%';
          discountBadgeEl.style.display = '';
        }
      } else {
        if (comparePriceEl) comparePriceEl.style.display = 'none';
        if (discountBadgeEl) discountBadgeEl.style.display = 'none';
      }

    }

    /* Update sticky panel price */
    var stickyPrice = document.querySelector('.arif-sticky-panel-price');
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

    /* Update button state */
    if (submitBtn) {
      if (variant.available) {
        submitBtn.disabled = false;
        submitBtn.querySelector('span').textContent = 'ADD TO CART';
      } else {
        submitBtn.disabled = true;
        submitBtn.querySelector('span').textContent = 'SOLD OUT';
      }
    }

    /* Navigate to variant's featured media */
    if (variant.featured_media) {
      var mediaItem = document.querySelector('.arif-gallery-main-slide[data-media-id="' + variant.featured_media.id + '"]');
      if (mediaItem) {
        var idx = parseInt(mediaItem.dataset.index, 10);
        goToSlide(idx);
      }
    }

    /* Push URL state for deep linking */
    if (window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  /* -----------------------------------------------------------------------
     FORM — Async cart/add.js with theme cart drawer integration
     ----------------------------------------------------------------------- */
  function initFormInteractions() {
    var form = document.querySelector('#arif-main-product-form');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var submitBtn = form.querySelector('.arif-add-to-cart-cta');
      if (submitBtn && submitBtn.disabled) return;

      form.classList.add('loading');

      var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root_url)
        ? window.Shopify.routes.root_url
        : '/';
      if (rootUrl.charAt(rootUrl.length - 1) !== '/') rootUrl += '/';

      fetch(rootUrl + 'cart/add.js', {
        method: 'POST',
        body: new FormData(form)
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Cart add failed: ' + res.status);
          return res.json();
        })
        .then(function (data) {
          form.classList.remove('loading');

          /* Dispatch standard event for theme integration */
          document.dispatchEvent(
            new CustomEvent('cart:updated', { bubbles: true, detail: { product: data } })
          );

          /* Attempt to trigger the theme's native cart drawer */
          if (window.theme && typeof window.theme.openCartDrawer === 'function') {
            window.theme.openCartDrawer();
          }
        })
        .catch(function () {
          form.classList.remove('loading');
        });
    });
  }

  /* -----------------------------------------------------------------------
     ACCORDIONS — Exclusive open behavior
     ----------------------------------------------------------------------- */
  function initAccordions() {
    var details = document.querySelectorAll('.arif-accordion-item');
    details.forEach(function (target) {
      target.querySelector('.arif-accordion-header').addEventListener('click', function () {
        /* When clicking to open (open attr not yet set), close others */
        if (!target.hasAttribute('open')) {
          details.forEach(function (d) {
            if (d !== target) d.removeAttribute('open');
          });
        }
      });
    });
  }

  /* -----------------------------------------------------------------------
     STICKY PANEL — IntersectionObserver on main CTA
     ----------------------------------------------------------------------- */
  function initStickyPanel() {
    var panel = document.getElementById('arif-sticky-panel');
    var mainCta = document.querySelector('.arif-add-to-cart-cta');
    if (!panel || !mainCta || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var isVisible = entry.isIntersecting;
          panel.classList.toggle('visible', !isVisible);
          panel.setAttribute('aria-hidden', isVisible ? 'true' : 'false');
        });
      },
      { threshold: 0, rootMargin: '0px' }
    );

    observer.observe(mainCta);
  }

  /* -----------------------------------------------------------------------
     QUANTITY ADJUSTER
     ----------------------------------------------------------------------- */
  function adjustQty(btn, step) {
    var input = btn.closest('.arif-quantity-selector-module').querySelector('.arif-qty-input');
    if (!input) return;
    var val = (parseInt(input.value, 10) || 1) + step;
    input.value = val < 1 ? 1 : val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* -----------------------------------------------------------------------
     GALLERY ZOOM — CSS transform-origin tracking
     ----------------------------------------------------------------------- */
  /* -----------------------------------------------------------------------
     GALLERY MANAGER
     ----------------------------------------------------------------------- */
  var currentSlideIndex = 0;
  var gallerySlides = [];
  var thumbBtns = [];
  var lightboxSlides = [];
  var lightboxActive = false;

  function initGallery() {
    var container = document.querySelector('.arif-gallery-container');
    if (!container) return;

    gallerySlides = Array.from(container.querySelectorAll('.arif-gallery-main-slide'));
    thumbBtns = Array.from(container.querySelectorAll('.arif-gallery-thumb-btn'));

    // Main navigation buttons
    var mainPrev = container.querySelector('.arif-gallery-main-display .prev-btn');
    var mainNext = container.querySelector('.arif-gallery-main-display .next-btn');

    if (mainPrev) {
      mainPrev.addEventListener('click', function(e) {
        e.stopPropagation();
        navigateGallery(-1);
      });
    }
    if (mainNext) {
      mainNext.addEventListener('click', function(e) {
        e.stopPropagation();
        navigateGallery(1);
      });
    }

    // Thumbnail clicks
    thumbBtns.forEach(function (btn) {
      btn.addEventListener('click', function() {
        var idx = parseInt(btn.dataset.index, 10);
        goToSlide(idx);
      });
    });

    // Main image click to open lightbox
    gallerySlides.forEach(function (slide) {
      var img = slide.querySelector('.arif-gallery-main-img');
      if (img) {
        img.style.cursor = 'zoom-in';
        img.addEventListener('click', function() {
          var idx = parseInt(slide.dataset.index, 10);
          openLightbox(idx);
        });
      }
    });

    // Handle lightbox positioning and setups
    var lightbox = container.querySelector('.arif-lightbox-modal');
    if (lightbox) {
      lightboxSlides = Array.from(lightbox.querySelectorAll('.arif-lightbox-slide'));
      
      var lightboxPrev = lightbox.querySelector('.prev-btn');
      var lightboxNext = lightbox.querySelector('.next-btn');

      if (lightboxPrev) {
        lightboxPrev.addEventListener('click', function(e) {
          e.stopPropagation();
          navigateLightbox(-1);
        });
      }
      if (lightboxNext) {
        lightboxNext.addEventListener('click', function(e) {
          e.stopPropagation();
          navigateLightbox(1);
        });
      }

      // Move lightbox element to body to escape transformed parent containers (like quick views)
      document.body.appendChild(lightbox);
    }

    // Keydown for lightbox navigation
    document.addEventListener('keydown', function(e) {
      if (!lightboxActive) return;
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        navigateLightbox(-1);
      } else if (e.key === 'ArrowRight') {
        navigateLightbox(1);
      }
    });

    // Initialize global exposure for inline HTML events
    window.ArifGallery = {
      handleZoom: handleZoom,
      handleZoomReset: handleZoomReset,
      handleLightboxZoom: handleLightboxZoom,
      handleLightboxZoomReset: handleLightboxZoomReset,
      closeLightbox: closeLightbox
    };
  }

  function goToSlide(idx) {
    if (idx < 0 || idx >= gallerySlides.length) return;
    currentSlideIndex = idx;

    gallerySlides.forEach(function (slide, i) {
      slide.classList.toggle('active', i === idx);
      
      // Pause any video if switching slides
      if (i !== idx) {
        var video = slide.querySelector('video');
        if (video) video.pause();
        var iframe = slide.querySelector('iframe');
        if (iframe) {
          var src = iframe.src;
          iframe.src = src; // reload to stop playback
        }
      }
    });

    thumbBtns.forEach(function (btn, i) {
      btn.classList.toggle('active', i === idx);
    });

    // Center active thumbnail in scroll container
    var activeThumb = thumbBtns[idx];
    if (activeThumb) {
      var wrapper = activeThumb.closest('.arif-gallery-thumbnails-scroll');
      if (wrapper) {
        var left = activeThumb.offsetLeft - (wrapper.clientWidth / 2) + (activeThumb.clientWidth / 2);
        wrapper.scrollTo({ left: left, behavior: 'smooth' });
      }
    }
  }

  function navigateGallery(step) {
    var newIdx = currentSlideIndex + step;
    if (newIdx < 0) {
      newIdx = gallerySlides.length - 1;
    } else if (newIdx >= gallerySlides.length) {
      newIdx = 0;
    }
    goToSlide(newIdx);
  }

  function openLightbox(idx) {
    var modal = document.querySelector('.arif-lightbox-modal');
    if (!modal) return;

    lightboxActive = true;
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('active');
    document.body.style.overflow = 'hidden';

    goToLightboxSlide(idx);
  }

  function closeLightbox() {
    var modal = document.querySelector('.arif-lightbox-modal');
    if (!modal) return;

    lightboxActive = false;
    modal.setAttribute('aria-hidden', 'true');
    modal.classList.remove('active');
    document.body.style.overflow = '';
  }

  function goToLightboxSlide(idx) {
    var targetSlide = lightboxSlides.find(function(slide) {
      return parseInt(slide.dataset.index, 10) === idx;
    });

    if (targetSlide) {
      lightboxSlides.forEach(function (slide) {
        slide.classList.remove('active');
      });
      targetSlide.classList.add('active');
      currentLightboxIndex = idx;
    } else {
      var imageSlide = lightboxSlides[0];
      if (imageSlide) {
        lightboxSlides.forEach(function (slide) {
          slide.classList.remove('active');
        });
        imageSlide.classList.add('active');
        currentLightboxIndex = parseInt(imageSlide.dataset.index, 10);
      }
    }
  }

  var currentLightboxIndex = 0;

  function navigateLightbox(step) {
    if (lightboxSlides.length === 0) return;
    
    var currentArrIdx = lightboxSlides.findIndex(function(slide) {
      return parseInt(slide.dataset.index, 10) === currentLightboxIndex;
    });

    var newArrIdx = currentArrIdx + step;
    if (newArrIdx < 0) {
      newArrIdx = lightboxSlides.length - 1;
    } else if (newArrIdx >= lightboxSlides.length) {
      newArrIdx = 0;
    }

    var targetSlide = lightboxSlides[newArrIdx];
    if (targetSlide) {
      var realIdx = parseInt(targetSlide.dataset.index, 10);
      goToLightboxSlide(realIdx);
    }
  }

  // Zoom functions
  function handleZoom(e) {
    var img = e.currentTarget.querySelector('.arif-gallery-main-img');
    if (!img) return;
    var rect = e.currentTarget.getBoundingClientRect();
    var x = ((e.clientX - rect.left) / rect.width) * 100;
    var y = ((e.clientY - rect.top) / rect.height) * 100;
    img.style.transformOrigin = x + '% ' + y + '%';
    img.style.transform = 'scale(1.6)';
  }

  function handleZoomReset(e) {
    var img = e.currentTarget.querySelector('.arif-gallery-main-img');
    if (img) {
      img.style.transform = 'scale(1)';
      img.style.transformOrigin = 'center center';
    }
  }

  function handleLightboxZoom(e) {
    var img = e.currentTarget.querySelector('.arif-lightbox-img');
    if (!img) return;
    var rect = e.currentTarget.getBoundingClientRect();
    var x = ((e.clientX - rect.left) / rect.width) * 100;
    var y = ((e.clientY - rect.top) / rect.height) * 100;
    img.style.transformOrigin = x + '% ' + y + '%';
    img.style.transform = 'scale(2)';
  }

  function handleLightboxZoomReset(e) {
    var img = e.currentTarget.querySelector('.arif-lightbox-img');
    if (img) {
      img.style.transform = 'scale(1)';
      img.style.transformOrigin = 'center center';
    }
  }

  /* -----------------------------------------------------------------------
     CURRENCY FORMATTER
     ----------------------------------------------------------------------- */
  function formatMoney(cents) {
    var format = (window.Shopify && window.Shopify.money_format)
      ? window.Shopify.money_format
      : '${{amount}}';

    var amount = (cents / 100.0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    return format.replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  /* -----------------------------------------------------------------------
     PUBLIC API
     ----------------------------------------------------------------------- */
  return {
    init: init,
    adjustQty: adjustQty,
    goToSlide: goToSlide
  };
})();

document.addEventListener('DOMContentLoaded', ArifProduct.init);

